import { useCallback, useEffect, useState } from "react";
import type { GetToken } from "../../../controllers/eqmsApi";
import {
  diffExternalDocRevisions,
  downloadExternalDocRevision,
  fetchExternalDocRevisionBytes,
  listExternalDocRevisions,
  uploadExternalDocFile,
} from "../../../controllers/eqmsApi";
import { friendlyMessage } from "../../../controllers/projectApi";
import { FileDrop } from "../../components/FileDrop";
import { DocPreviewModal } from "../../components/DocPreviewModal";
import type { ExternalDocRevision, RevisionDiff } from "../../../models/eqms";

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The file history of one external document: upload a new version, download any past one, and — the
 * point of keeping them — see what changed between two of them.
 *
 * A new edition of a standard used to overwrite the link and leave no trace of what it replaced. Now
 * each upload is a revision, and the diff against the previous one opens on its own so a reviewer
 * does not have to download both editions and compare by eye.
 */
export function ExternalDocRevisions({
  getToken,
  documentId,
  onUploaded,
}: {
  getToken: GetToken;
  documentId: string;
  /** The parent list shows the current filename, so it has to reload after an upload. */
  onUploaded: () => void;
}) {
  const [revisions, setRevisions] = useState<ExternalDocRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changeSummary, setChangeSummary] = useState("");
  const [diff, setDiff] = useState<RevisionDiff | null>(null);
  const [previewing, setPreviewing] = useState<ExternalDocRevision | null>(null);
  const [diffing, setDiffing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRevisions(await listExternalDocRevisions(getToken, documentId));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken, documentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    try {
      await uploadExternalDocFile(getToken, documentId, file, changeSummary || undefined);
      setChangeSummary("");
      const next = await listExternalDocRevisions(getToken, documentId);
      setRevisions(next);
      onUploaded();
      // The reason for uploading again is almost always "what changed?" — answer it without a
      // second click, exactly when there is a previous version to compare against.
      if (next.length >= 2) {
        await showDiff(next[1], next[0]);
      }
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function showDiff(from: ExternalDocRevision, to: ExternalDocRevision) {
    setDiffing(true);
    setError(null);
    try {
      setDiff(await diffExternalDocRevisions(getToken, documentId, from.id, to.id));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setDiffing(false);
    }
  }

  return (
    <div className="info-card" style={{ marginTop: 12 }}>
      <div className="home-card-head">
        <h4>파일 이력 · {revisions.length}</h4>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 12 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      <div className="ext-upload">
        <input
          className="sdui-input"
          placeholder="변경 요약 (선택)"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
        />
        <FileDrop
          compact
          disabled={busy}
          onFile={(f) => void upload(f)}
          label={busy ? "업로드 중…" : "새 버전을 여기에 끌어다 놓으세요"}
          hint={revisions.length > 0
            ? "올리면 이전 버전과의 변경점이 바로 아래 열립니다"
            : "클릭해서 고를 수도 있습니다"}
        />
      </div>

      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : revisions.length === 0 ? (
        <div className="sdui-list-empty">아직 올린 파일이 없습니다. 파일을 선택하면 첫 버전이 됩니다.</div>
      ) : (
        <div className="sdui-table-wrap">
          <table className="sdui-table tbl-title2">
            <thead>
              <tr>
                <th>버전</th>
                <th>파일명</th>
                <th>변경 요약</th>
                <th>크기</th>
                <th>올린 날짜</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {revisions.map((r, i) => {
                // revisions are newest-first, so the previous version is the next row down.
                const previous = revisions[i + 1];
                return (
                  <tr key={r.id}>
                    <td className="mono">v{r.revNo}</td>
                    <td>
                      {/* 파일명 자체가 미리보기 진입점 — 행마다 버튼을 하나 더 두는 것보다 곧바르다. */}
                      <button className="filename-link" onClick={() => setPreviewing(r)}>
                        {r.filename || `v${r.revNo}`}
                      </button>
                    </td>
                    <td>{r.changeSummary || "-"}</td>
                    <td className="mono">{fmtSize(r.size)}</td>
                    <td className="mono">{new Date(r.createdAt).toLocaleString()}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        {previous && (
                          <button
                            className="sdui-button sdui-button-secondary"
                            disabled={diffing}
                            onClick={() => void showDiff(previous, r)}
                          >
                            {/* 조사를 붙이면 숫자에 따라 와/과가 갈린다(v1→과, v2→와). 아예 피한다. */}
                            v{previous.revNo} 대비 변경
                          </button>
                        )}
                        <button
                          className="sdui-button sdui-button-secondary"
                          onClick={() => void downloadExternalDocRevision(
                            getToken, documentId, r.id, r.filename || `v${r.revNo}`)}
                        >
                          다운로드
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {diffing && <div className="center">비교하는 중…</div>}
      {diff && <DiffPanel diff={diff} onClose={() => setDiff(null)} />}

      {previewing && (
        <DocPreviewModal
          filename={previewing.filename || `v${previewing.revNo}`}
          subtitle={`v${previewing.revNo}${previewing.changeSummary ? ` · ${previewing.changeSummary}` : ""}`}
          load={() => fetchExternalDocRevisionBytes(getToken, documentId, previewing.id)}
          onDownload={() => void downloadExternalDocRevision(
            getToken, documentId, previewing.id, previewing.filename || `v${previewing.revNo}`)}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

function DiffPanel({ diff, onClose }: { diff: RevisionDiff; onClose: () => void }) {
  return (
    <div className="diff-panel">
      <div className="diff-head">
        <span>
          <b>v{diff.from.revNo} → v{diff.to.revNo}</b>
          {diff.textual && !diff.identical && (
            <span className="diff-counts">
              <span className="diff-add">+{diff.added}</span>
              <span className="diff-del">−{diff.removed}</span>
            </span>
          )}
        </span>
        <button className="link-btn" onClick={onClose}>닫기</button>
      </div>

      {!diff.textual ? (
        // Diffing a PDF or a docx byte by byte tells a reviewer nothing, so say what IS known.
        <div className="note">
          텍스트로 읽을 수 있는 형식이 아니라 줄 단위 비교를 할 수 없습니다
          {diff.from.contentHash && diff.to.contentHash && (
            <> · 내용은 {diff.from.contentHash === diff.to.contentHash ? "동일합니다" : "서로 다릅니다"}</>
          )}
          {" · "}크기 {fmtSize(diff.from.size)} → {fmtSize(diff.to.size)}
        </div>
      ) : diff.identical ? (
        <div className="sdui-list-empty">두 버전의 내용이 완전히 같습니다.</div>
      ) : (
        <>
          {diff.truncated && (
            <div className="note" style={{ marginBottom: 8 }}>
              파일이 매우 길어 앞부분만 비교했습니다.
            </div>
          )}
          <div className="diff-body">
            {diff.lines.map((l, i) =>
              l.kind === "GAP" ? (
                <div key={i} className="diff-line gap">⋯</div>
              ) : (
                <div key={i} className={`diff-line ${l.kind.toLowerCase()}`}>
                  <span className="diff-no">{l.oldNo || ""}</span>
                  <span className="diff-no">{l.newNo || ""}</span>
                  <span className="diff-sign">
                    {l.kind === "ADDED" ? "+" : l.kind === "REMOVED" ? "−" : " "}
                  </span>
                  <span className="diff-text">{l.text || " "}</span>
                </div>
              ),
            )}
          </div>
        </>
      )}
    </div>
  );
}
